import { useCombobox } from 'downshift';
import { useCallback, useEffect } from 'react';
import { isCypressWindow } from '../../../utils/envCypress';
import type { UseAutocompleteOutput, UseAutocompleteProps } from '../Autocomplete/types';
import { getLabelWrapper, useAutocomplete } from '../Autocomplete/useAutocomplete';
import type { UseSearchSelectProps } from './types';

/**
 * A wrapper of useAutocomplete which select-ifies the behavior.
 */
export function useSearchSelect<T>(props: UseSearchSelectProps<T>): UseAutocompleteOutput<T> {
  const {
    getLabel,
    inputRef,
    selection,
    onChange,
    closeDropdownOnItemSelection = true,
    closeOnInputBlur = true,
    inputValueChangeOnItemSelection = 'set',
    enableTabSelect = true,
    scrollSelectionIntoViewIndex,
  } = props;

  const { onIsOpenChange, ...restOfProps } = props;

  const onSelectedItemChange = useCallback<NonNullable<UseAutocompleteProps<T>['onSelectedItemChange']>>(
    ({ selectedItem, type, highlightedIndex }) => {
      const haveSelectedItem = selectedItem != null;
      const selectedItemIsDisabled = haveSelectedItem && props.isItemDisabled?.(selectedItem);
      const shouldReturn = !haveSelectedItem || selectedItemIsDisabled;
      if (shouldReturn) {
        return;
      }
      // When we press tab downshift automatically triggers onSelectItemChange, the only caveat is that highlightedIndex is -1
      // So we simply need to ignore the highlightedIndex for this specific case (not early return) and proceed on selecting the item
      if (type !== useCombobox.stateChangeTypes.ItemClick) {
        // Lets only do this when we are 100% sure the change was not caused by a click
        // https://talostrading.atlassian.net/browse/DEAL-4207 - outlines a bug where if the
        // first item in the list is disabled, and user clicks any item in list, it would not register.
        const shouldIgnoreTabSelect = !enableTabSelect && highlightedIndex === -1;
        const isTabSelectInvalid =
          enableTabSelect && highlightedIndex === -1 && type !== useCombobox.stateChangeTypes.InputBlur;
        if (shouldIgnoreTabSelect || isTabSelectInvalid) {
          return;
        }
      }

      onChange(selectedItem);
      if (closeDropdownOnItemSelection) {
        // Also blur the input on item selection to push the tab index forward to next form element
        inputRef.current?.blur();
      }
    },
    [closeDropdownOnItemSelection, enableTabSelect, inputRef, onChange, props]
  );
  const autocomplete = useAutocomplete({
    stateReducer: (state, action) => {
      // Clear input value whenever dropdown is opening (transitioning from closed to open)
      const isOpening = !state.isOpen && action.changes.isOpen === true;
      let changesWithClearedInput = action.changes;

      if (isOpening) {
        changesWithClearedInput = { ...action.changes, inputValue: '' };
      }

      switch (action.type) {
        case useCombobox.stateChangeTypes.InputClick: {
          // Allow dropdown to open when closed, but preserve open state when already open
          // This prevents closing the dropdown when clicking inside the search input to edit the query
          return {
            ...changesWithClearedInput,
            // For backward compatibility with earlier combobox behavior,
            // keep the highlighted index when the input is clicked (but allow opening)
            highlightedIndex: isOpening ? (action.changes.highlightedIndex ?? 0) : state.highlightedIndex,
            // Allow opening when closed, but preserve state when already open to prevent accidental closing
            isOpen: isOpening ? changesWithClearedInput.isOpen : state.isOpen,
            // changesWithClearedInput already has inputValue cleared if opening
            inputValue: changesWithClearedInput.inputValue ?? state.inputValue,
          };
        }
        case useCombobox.stateChangeTypes.InputBlur: {
          const newInputValue =
            inputValueChangeOnItemSelection === 'clear'
              ? ''
              : inputValueChangeOnItemSelection === 'keep'
                ? state.inputValue
                : changesWithClearedInput.inputValue; // this'll be downshift's default behavior (the selection's label)

          return {
            ...changesWithClearedInput,
            isOpen: closeOnInputBlur ? false : state.isOpen, // if closeOnInputBlur is true, we close the dropdown, otherwise we keep it open
            inputValue: newInputValue ?? state.inputValue, // fallback to state.inputValue if for some reason inputValue becomes undefined
          };
        }
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
        case useCombobox.stateChangeTypes.ItemClick: {
          const isProgrammaticChange = action.type === useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem;

          // If this is a programmatic update (ControlledPropUpdatedSelectedItem) and the dropdown just opened,
          // don't set the inputValue to the selected item's label - keep it cleared
          // We check if state.isOpen is true (dropdown is now open) and inputValue was just cleared (empty string)
          const shouldClearInput = isProgrammaticChange && state.isOpen && state.inputValue === '';

          const newInputValue = shouldClearInput
            ? ''
            : inputValueChangeOnItemSelection === 'clear'
              ? ''
              : inputValueChangeOnItemSelection === 'keep'
                ? state.inputValue
                : changesWithClearedInput.inputValue; // this'll be downshift's default behavior (the selection's label)

          return {
            ...changesWithClearedInput,
            // If programmatic changes, keep state, else close if we are configured to close on item selection
            isOpen: isProgrammaticChange ? state.isOpen : !closeDropdownOnItemSelection,
            highlightedIndex: closeDropdownOnItemSelection
              ? changesWithClearedInput.highlightedIndex
              : state.highlightedIndex, // if we're not closing the dropdown, leave the highlighted index as is!
            inputValue: newInputValue ?? state.inputValue, // fallback to state.inputValue if for some reason inputValue becomes undefined
          };
        }
        case useCombobox.stateChangeTypes.FunctionOpenMenu: {
          const nextState = {
            ...changesWithClearedInput,
            inputValue: '', // Explicitly clear input for FunctionOpenMenu
          };
          if (scrollSelectionIntoViewIndex) {
            nextState.highlightedIndex = state.selectedItem
              ? scrollSelectionIntoViewIndex(props.items, state.selectedItem)
              : 0;
          }
          return nextState;
        }
        case useCombobox.stateChangeTypes.InputChange: {
          return {
            ...changesWithClearedInput,
            highlightedIndex: 0, // reset the highlightedIndex every time the input value changes
          };
        }
        case useCombobox.stateChangeTypes.MenuMouseLeave: {
          return {
            ...changesWithClearedInput,
            highlightedIndex: state.highlightedIndex, // Do not scroll to top if mouse leaves input
          };
        }
        case useCombobox.stateChangeTypes.FunctionSetInputValue: {
          // This action seems to be called when downshift tries to programmatically set the inputValue to be the label of the selectedItem.
          // In our searchselects, we never want to prime the input field value to be that of the selected item.
          // There are other cases where we programmatically try to reset the input value ourselves. This we allow:
          if (changesWithClearedInput.inputValue === '') {
            return changesWithClearedInput;
          }

          return state;
        }
        default:
          return changesWithClearedInput;
      }
    },
    selectedItem: selection ?? null, // this component is strictly controlled, we do not allow selectedItem to be undefined.
    itemToString: item => (item ? `${getLabelWrapper(getLabel, item)}` : ''),
    onSelectedItemChange: selectedItemChangeProps => {
      // HACK (setTimeout wrapper) - In Cypress only, Downshift and Immer (RTK) don't play well together on
      // immediate calls after Redux v9 update causing infinite item change calls to onSelectedItemChange
      // - Seen in HistoricalBalanceReport.cy.tsx test: 'allows you to trade your spot positions (balances)'
      // https://github.com/downshift-js/downshift/issues/1597
      // https://github.com/downshift-js/downshift/issues/1511#issuecomment-1730429456
      if (isCypressWindow(window)) {
        setTimeout(() => onSelectedItemChange(selectedItemChangeProps), 0);
        return;
      }
      onSelectedItemChange(selectedItemChangeProps);
    },
    onIsOpenChange: changes => {
      // Focus input when dropdown opens (input clearing is handled by stateReducer)
      if (changes.isOpen) {
        inputRef.current?.focus();
      }
      onIsOpenChange?.(changes);
    },
    ...restOfProps,
  });

  const { setInputValue } = autocomplete;

  useEffect(() => {
    if (props.selection == null) {
      setInputValue('');
    }
  }, [props.selection, setInputValue]);

  return autocomplete;
}
